import { ApprovalScopeEnum, PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { generatePath, Link } from "react-router-dom"

import DeletedTooltip from "Components/deletedTooltip"
import { VendorField, VendorFieldProps } from "Features/pages/expenses/expenseDetails/expenseSourceSection/VendorField"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { ApprovalProcessService, NotificationService } from "Shared/services"
import { useVendor } from "../../../../shared/queries/vendors/vendor.queries"
import { useMutatePurchaseOrders } from "../hooks/usePurchaseOrders.hook"
import { useBuildApprovalMutation } from "../queries/purchaseOrder.queries"
import { ILineItem } from "../types/LineItems.interfaces"
import { PurchaseOrder } from "../types/PurchaseOrder.interfaces"

interface PurchaseOrderVendorFieldProps {
    purchaseOrder: PurchaseOrder
    isReadOnly?: boolean
    areInputsDisabled: boolean
}

const mapLineItemsToDTO = (lineItems: Array<ILineItem>) =>
    lineItems.map((item) => ({
        costCenter: item.costCenter,
        costCenter2: item.costCenter2,
        expenseAccount: item.expenseAccount,
        link: item.link,
        materialCode: item.materialCode,
        name: item.name,
        note: item.note,
        quantity: item.quantity,
        taxRate: item.taxRate?._id,
        totalGrossPrice: item.totalGrossPrice,
        totalNetPrice: item.totalNetPrice,
        totalTaxPrice: item.totalTaxPrice,
        unitOfMeasure: item.unitOfMeasure,
        unitPrice: item.unitPrice,
    }))

export const PurchaseOrderVendorField = ({ purchaseOrder, isReadOnly, areInputsDisabled }: PurchaseOrderVendorFieldProps) => {
    const { t } = useTranslation()
    const { updatePO } = useMutatePurchaseOrders()
    const buildApprovalProcess = useBuildApprovalMutation()

    const [isVendorUpdating, setIsVendorUpdating] = useState(false)

    // Fetch full vendor data to check approval status
    const { data: purchaseOrderVendor } = useVendor(purchaseOrder?.vendor?._id, !!purchaseOrder?.vendor?._id)

    // Check if purchase order is in a state that allows updates
    const isPurchaseOrderEditable = purchaseOrder && !PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN.includes(purchaseOrder.state.currentState)

    const onVendorUpdate = async (value: string) => {
        try {
            setIsVendorUpdating(true)

            // Map line items to DTO format
            const lineItemsDTO = mapLineItemsToDTO(purchaseOrder.lineItems)

            // Build new approval processes with vendor change
            const newApprovalProcesses = await buildApprovalProcess.mutateAsync({
                approvalProcessScopes: [ApprovalScopeEnum.PURCHASE_ORDER],
                purchaseOrderData: {
                    lineItems: lineItemsDTO,
                    totalNetPrice: purchaseOrder.totalNetPrice,
                    currency: purchaseOrder.currency,
                    createCard: purchaseOrder.createCard,
                    vendor: { _id: value },
                    requestedBy: { _id: purchaseOrder.requestedBy._id },
                },
            })

            // Merge approval processes with confirmation dialog
            const mergedApprovalProcesses = await ApprovalProcessService.getMergedApprovalProcesses(purchaseOrder.approvalProcesses, newApprovalProcesses, {
                awaitConfirmation: true,
                prioritizeApproved: false,
            })

            if (!mergedApprovalProcesses) {
                return
            }

            // Update purchase order via API with new approval processes
            await updatePO(purchaseOrder._id, {
                vendor: value,
                description: purchaseOrder.description,
                dateOfIssue: purchaseOrder.dateOfIssue,
                deliveryLocation: purchaseOrder.deliveryLocation,
                expectedFullfilmentDate: purchaseOrder.expectedFullfilmentDate,
                isService: purchaseOrder.isService,
                lineItems: lineItemsDTO,
                paymentTerms: purchaseOrder.paymentTerms,
                performancePeriod:
                    purchaseOrder.performanceDateFrom && purchaseOrder.performanceDateTo ? [purchaseOrder.performanceDateFrom, purchaseOrder.performanceDateTo] : null,
                purchaseOrderDocument: purchaseOrder.purchaseOrderDocument,
                purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
                requestedBy: purchaseOrder.requestedBy._id,
                tags: purchaseOrder.tags,
                totalGrossPrice: purchaseOrder.totalGrossPrice,
                totalNetPrice: purchaseOrder.totalNetPrice,
                totalTaxPrice: purchaseOrder.totalTaxPrice,
                currency: purchaseOrder.currency,
                createCard: purchaseOrder.createCard,
                approvalProcesses: mergedApprovalProcesses,
            })
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsVendorUpdating(false)
        }
    }

    // For purchase orders, we need to handle the display differently
    // ExtendedReferencedVendor doesn't have all the properties, so we use the fetched vendor
    const currentCreditor = purchaseOrderVendor
    const embeddedCreditor = purchaseOrder?.vendor

    // Determine if we should show select or details
    const shouldShowSelect = () => {
        if (isReadOnly || !isPurchaseOrderEditable) {
            return false
        }
        return !currentCreditor || !(currentCreditor as Vendor)?.isApproved
    }

    // For purchase orders, show simple vendor link (no user creditors)
    if (!shouldShowSelect() && embeddedCreditor) {
        return (
            <li className="flex justify-between items-start">
                <span className="title">{t("label:vendor")}</span>
                <div className="flex gap-4 min-w-0 max-w-full">
                    {purchaseOrderVendor?.deleted && <DeletedTooltip translationKey="tooltips:vendor.was_deleted" deletionDate={purchaseOrderVendor.dateDeleted} />}
                    <Link className="block truncate max-w-full" to={generatePath(RoutePathEnum.VENDOR_DETAIL_PAGE, { id: purchaseOrder.vendor?._id! })}>
                        {embeddedCreditor.name}
                    </Link>
                </div>
            </li>
        )
    }

    const vendorFieldProps: VendorFieldProps = {
        currentCreditor,
        embeddedCreditor,
        isUserCreditor: false, // Purchase orders don't have user creditors
        selectedCreditorId: currentCreditor ? ((currentCreditor as Vendor)?.id ?? "") : "",
        selectedVendorType: VendorTypeEnum.VENDOR,
        shouldRenderSelectDropdown: false,
        onVendorUpdate: shouldShowSelect() ? onVendorUpdate : undefined,
        isReadOnly: isReadOnly || !isPurchaseOrderEditable,
        areInputsDisabled,
        isLoading: isVendorUpdating,
        showUserCreditors: false,
    }

    return <VendorField {...vendorFieldProps} />
}
